import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';

interface User {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
  is_admin: boolean;
  email_change_pending?: string | null;
  disclaimer_acknowledged?: boolean;
  fais_cat_ii_compliant?: boolean;
  fais_cat_ii_compliant_since?: string | null;
  fais_cat_ii_compliance_valid_until?: string | null;
  fais_cat_ii_certification_institution?: string | null;
  fais_cat_ii_certification_number?: string | null;
  org_id?: number | null;  // v28-3-5: set when user is an org member
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  disclaimerAcknowledged: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName: string) => Promise<{ invite_accepted: boolean; invite_seat_tier: string | null; org_name: string | null; org_id: number | null }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  /** Called by DisclaimerModal after API call succeeds to dismiss the modal */
  setDisclaimerAcknowledged: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);
  const [disclaimerAcknowledged, setDisclaimerAcknowledged] = useState(true);

  // Ref holding the active proactive-refresh timer so we can clear it on logout
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref so the timer callback always invokes the latest scheduleTokenRefresh (avoids stale closures)
  const scheduleTokenRefreshRef = useRef<(t: string) => void>(() => {});

  // Proactive token refresh: decode the JWT exp claim and schedule a silent refresh
  // 5 minutes before expiry. Stored in a ref so timer callbacks are never stale.
  scheduleTokenRefreshRef.current = (accessToken: string) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const delay = payload.exp * 1000 - Date.now() - 5 * 60 * 1000; // 5 min before expiry
      if (delay <= 0) return; // already expired — reactive 401 path handles it
      refreshTimerRef.current = setTimeout(async () => {
        const newToken = await attemptRefresh();
        if (newToken) {
          setToken(newToken);
          scheduleTokenRefreshRef.current(newToken); // schedule next cycle
        }
        // If refresh fails here the next API call will trigger reactive 401 handling
      }, delay);
    } catch {
      // Malformed JWT — skip proactive scheduling; reactive 401 path still covers us
    }
  };

  // On mount: verify existing token and start proactive refresh schedule
  useEffect(() => {
    if (token) {
      fetchUser();
      scheduleTokenRefreshRef.current(token);
    } else {
      setIsLoading(false);
    }
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reschedule whenever the token is replaced (login or post-refresh setToken)
  useEffect(() => {
    if (!token) return;
    scheduleTokenRefreshRef.current(token);
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [token]);

  // Sync disclaimerAcknowledged whenever user changes
  useEffect(() => {
    if (user) {
      setDisclaimerAcknowledged(user.disclaimer_acknowledged ?? false);
    } else {
      // Not logged in — no modal needed
      setDisclaimerAcknowledged(true);
    }
  }, [user]);

  const clearAuth = () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
    setDisclaimerAcknowledged(true);
  };

  const fetchUser = async (authToken?: string) => {
    const tokenToUse = authToken || token;
    if (!tokenToUse) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/v1/auth/me`, {
        headers: {
          Authorization: `Bearer ${tokenToUse}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else if (response.status === 401) {
        // Try to silently refresh before giving up
        const newToken = await attemptRefresh();
        if (newToken) {
          const retryResponse = await fetch(`${API_URL}/api/v1/auth/me`, {
            headers: { Authorization: `Bearer ${newToken}` },
          });
          if (retryResponse.ok) {
            setUser(await retryResponse.json());
            setToken(newToken);
          } else {
            clearAuth();
          }
        } else {
          clearAuth();
        }
      } else {
        clearAuth();
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  const attemptRefresh = async (): Promise<string | null> => {
    const storedRefreshToken = localStorage.getItem('refresh_token');
    if (!storedRefreshToken) return null;

    try {
      const response = await fetch(`${API_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: storedRefreshToken }),
      });

      if (!response.ok) return null;

      const data = await response.json();
      localStorage.setItem('token', data.access_token);
      return data.access_token;
    } catch {
      return null;
    }
  };

  const login = async (email: string, password: string) => {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);

    const response = await fetch(`${API_URL}/api/v1/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();
    localStorage.setItem('token', data.access_token);
    if (data.refresh_token) {
      localStorage.setItem('refresh_token', data.refresh_token);
    }
    setToken(data.access_token);

    // Fetch user immediately so isAuthenticated is true before navigation
    await fetchUser(data.access_token);
  };

  const register = async (email: string, password: string, fullName: string) => {
    const response = await fetch(`${API_URL}/api/v1/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        password,
        full_name: fullName,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Registration failed');
    }

    const regData = await response.json();

    // Auto-login after registration — wrap separately so callers can distinguish
    // "registration failed" from "registration succeeded but login failed"
    try {
      await login(email, password);
    } catch {
      throw new Error('Account created. Please sign in to continue.');
    }

    return {
      invite_accepted: regData.invite_accepted ?? false,
      invite_seat_tier: regData.invite_seat_tier ?? null,
      org_name: regData.org_name ?? null,
      org_id: regData.org_id ?? null,
    };
  };

  const logout = () => {
    clearAuth();
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!user,
        isLoading,
        disclaimerAcknowledged,
        login,
        register,
        logout,
        refreshUser,
        setDisclaimerAcknowledged,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
